/**
 * Bundle component picker (SR bundles cart-transform).
 *
 * Keeps a hidden `properties[_bundle_selections]` line-item property in sync
 * with the customer's per-slot variant choices. The cart-transform function
 * reads that property at checkout to expand the single bundle line into its
 * real component variants.
 *
 * The property is a comma-separated list of numeric variant IDs carrying ONE
 * VALUE PER SLOT, IN ORDER, INCLUDING FIXED SLOTS. If the count doesn't match
 * the slot count, the function falls back to every slot's default - which
 * looks like it worked right up until someone's ProFlex Wide ships as a
 * Narrow. The Liquid pre-seeds the input with all defaults, and this replaces
 * values strictly by index so the count can never drift.
 */
(function () {
	class BundlePicker extends HTMLElement {
		constructor() {
			super();
			this.handleChange = this.handleChange.bind(this);
		}

		connectedCallback() {
			this.selectionsInput = this.querySelector('[data-bundle-selections]');
			this.slotCount = Number(this.dataset.slotCount || 0);

			if (!this.selectionsInput) return;

			// Guard the invariant at runtime as well as in Liquid: a mismatch here
			// means the line would silently expand to defaults.
			const seeded = this.selectionsInput.value.split(',').filter(Boolean);
			if (seeded.length !== this.slotCount) {
				console.warn(
					'[bundle-picker] seeded selections (' + seeded.length + ') do not match slot count (' +
					this.slotCount + '). Selections will fall back to defaults.'
				);
			}

			this.addEventListener('change', this.handleChange);
		}

		disconnectedCallback() {
			this.removeEventListener('change', this.handleChange);
		}

		handleChange(event) {
			const input = event.target;
			if (!input || !input.dataset || !input.dataset.variantId) return;

			const slot = input.closest('[data-slot-index]');
			if (!slot) return;

			const slotIndex = Number(slot.dataset.slotIndex);
			if (Number.isNaN(slotIndex)) return;

			const selections = this.selectionsInput.value.split(',');

			// Replace by index - never push. Appending would grow the list past the
			// slot count and trigger the all-defaults fallback.
			if (slotIndex >= selections.length) return;

			selections[slotIndex] = input.dataset.variantId;
			this.selectionsInput.value = selections.join(',');

			this.dispatchEvent(
				new CustomEvent('bundle-picker:change', {
					bubbles: true,
					detail: { slotIndex: slotIndex, variantId: input.dataset.variantId, selections: this.selectionsInput.value }
				})
			);
		}
	}

	if (!customElements.get('bundle-picker')) {
		customElements.define('bundle-picker', BundlePicker);
	}
})();
